import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import the useNavigate hook
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import StudentMetrics from "../../components/SchoolFees/Student/StudentMetrics";
import { useFeesProfile } from "../../DataStore/FeesProfileContext";
import ActivityLoader from "../../components/Common/ActivityLoader";
import NoDataPage from "../NoDataPage";
import functions from "../../util/functions";
import StudentProfileInfo from "../../components/SchoolFees/Student/StudentProfileInfo";
import StudentStatement from "../../components/SchoolFees/Student/StudentStatement";
import Pagination from "../../components/Common/Pagination";
import { useTheme } from "../../DataStore/ThemeContext";
import AlertDateSelector from "../../components/Report/AlertDateSelector";
import ConfirmationDialog from "../../components/Common/ConfirmationDialog";
import ajaxSchool from "../../util/remote/ajaxSchool";
import Notification from "../../components/Common/Notification";
import AlertNotification from "../../components/Common/AlertNotification";
import printContent from "../../util/araknerdlib";
import StudentAdvanceModal from "../../components/SchoolFees/Student/StudentAdvanceModal";
import RenderSecure from "../../util/RenderSecure";

const StudentProfilePage = () => {
  const { 
    profileInfo, 
    studentId, 
    feesBalance, 
    discountSum, 
    billSum, 
    paidSum, 
    feesStatement,  
    loadProfile, 
    metaData, 
    setCurrentPage, 
    getFeesStatement,
    dispatch
  } = useFeesProfile();
  const {openSettings} = useTheme();
  const navigate = useNavigate();
  const [showDateAlert, setShowDateAlert] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [info, setInfo] = useState(null);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const statementRef = useRef(); // Ref to enclose statement content for printing

  useEffect(()=>{
    // Redirect if no studentId
    if (!studentId) {
        navigate('/school-fees-overview'); // Navigate to the previous page
    }
    setInfo(null);
    loadProfile();
  }, [studentId])

  

  if (profileInfo === "404") {
    return <NoDataPage />;
  }

  const PageActions = () => (
    <>
      <RenderSecure code={['STUDENT_FEES_BILL']}>
        <ActionDropdownItem iconClass="fa fa-file-invoice" label="Bill Student" onClick={handleBilling}/>
      </RenderSecure>
      <RenderSecure code={['STUDENT_CUSTOM_BILL']}>
        <ActionDropdownItem iconClass="fa fa-file-invoice" label="Custom Billing..." onClick={handleCustomBilling}/>
      </RenderSecure>
      <RenderSecure code={['STUDENT_FEES_ADVANCE']}>
        <ActionDropdownItem iconClass="fa fa-money-bill-wave" label="Add Fees Advance" onClick={handleAddFeesAdvance}/>
      </RenderSecure>
      <RenderSecure code={['STUDENT_ASSIGN_FEES_GROUP']}>
        <ActionDropdownItem iconClass="fa fa-users" label="Assign Fees Group" onClick={handleAddToFeesGroup} />
      </RenderSecure>
      <RenderSecure code={['STUDENT_ACTIVATE']}>
        {profileInfo?.status === "1" && <ActionDropdownItem iconClass="fa fa-ban" label="Deactivate Student" onClick={()=>setShowDeactivate(true)} />}
      </RenderSecure>
      <RenderSecure code={['STUDENT_ACTIVATE']}>
        {profileInfo?.status === "0" && <ActionDropdownItem iconClass="fa fa-circle-check" label="Activate Student" onClick={()=>setShowActivate(true)} />}
      </RenderSecure>
      <RenderSecure code={['STUDENT_FEES_DISCOUNT']}>
        <ActionDropdownItem iconClass="fa fa-percent" label="Apply Fees Discount" onClick={handleDiscount}/>
      </RenderSecure>

      <ActionDropdownItem iconClass="fa fa-calendar-alt" label="Change Statement Date" onClick={()=>setShowDateAlert(true)} />
      <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={()=>printContent(statementRef, "Student Fees Statement")}/>
      <RenderSecure code={['STUDENT_DELETE']}>
          <ActionDropdownItem iconClass="fa fa-trash-alt" label="Delete Student" onClick={()=>setShowDelete(true)} className="text-danger"/>
      </RenderSecure>
      <ActionDropdownItem iconClass="fa fa-refresh" label="Refresh" onClick={loadProfile} />
    </>
  );

  const handleAddFeesAdvance = () =>{
    setShowAdvanceModal(true);
  }

  const handleAddToFeesGroup = () =>{
    openSettings("Assign Fees Group", "AssignFeesGroup", [studentId]);
  }

  const handleBilling = () =>{
    openSettings("Billing Preview", "BillingPreview", [studentId], "Proceed to Bill");
  }

  const handleCustomBilling = () =>{
    openSettings("Custom Billing", "CustomBilling", [studentId], "Proceed to Bill");
  }

  const handleDiscount = () =>{
    openSettings("Discount Student", "NewFeesDiscount", [studentId], "Apply Discount");
  }

  const handleStatementDate = (range) =>{
    dispatch({ type: 'openingDate', payload: range.opening_date });
    dispatch({ type: 'closingDate', payload: range.closing_date });
    getFeesStatement(true, range);
  }

  const handleDeactivate = async() =>{
    setInfo(<AlertNotification type="info" message="Deactivating student. Please wait..." iconClass="fa fa-info-circle" loader={true}/>);
    const server_response = await ajaxSchool.deactivateBilling(studentId);
    if(server_response.status === "OK"){
      setInfo(<Notification type="success" message={server_response.message} closable={false} />);
      loadProfile(true);
    }else{
      setInfo(<Notification type="danger" message={server_response.message} closable={false} />);
    }
  }
  const handleDelete = async() =>{
    setInfo(<AlertNotification type="info" message="Deleting student. Please wait..." iconClass="fa fa-info-circle" loader={true}/>);
    const server_response = await ajaxSchool.deleteStudent(studentId);
    if(server_response.status === "OK"){
      setInfo(<Notification type="success" message={server_response.message} closable={false} />);
      window.location.reload();
    }else{
      setInfo(<Notification type="danger" message={server_response.message} closable={false} />);
    }
  }

  const handleActivate = async() =>{
    setInfo(<AlertNotification type="info" message="Activating student. Please wait..." iconClass="fa fa-info-circle" loader={true}/>);
    const server_response = await ajaxSchool.activateBilling(studentId);
    if(server_response.status === "OK"){
      setInfo(<Notification type="success" message={server_response.message} closable={false} />);
      loadProfile(true);
    }else{
      setInfo(<Notification type="danger" message={server_response.message} closable={false} />);
    }
  }

  return (
    <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Student Profile"
          description="View and manage student school fees records."
          iconClass="fa fa-user-circle"
          PageActions={PageActions}
          sticky={true}
          Pagination={metaData ? <Pagination
            metaData={metaData}
            onPageChange={(page) => setCurrentPage(page)}
            loadMore={true}
    /> : <></>}
        />
        {info}
       <div className="row">
            <div className="col-12 mb-3">
              {functions.isObject(profileInfo) && <StudentProfileInfo profileInfo={profileInfo} />}
              {!profileInfo && <ActivityLoader />}
            </div>

            <div className="col-12 mb-3">
              <StudentMetrics billed={billSum} paid={paidSum} discounted={discountSum} feesBalance={feesBalance} />
            </div>


          

          {/* Statement: full width, maximum height */}
          <div className="col-12">

             <div className="card rac-card mt-4">
                  {/* Header */}
                  <div className="rac-head d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                      <div className="rac-headIcon">
                        <i className="fa fa-book-open" />
                      </div>
                      <div>
                        <h6 className="rac-title">Fees statement</h6>
                        <div className="rac-sub">Student statement of accounts</div>
                      </div>
                    </div>
            
                  </div>


           

              <div className="yx-card-body yx-statementBody">
                {functions.isObject(feesStatement) && (
                  <StudentStatement
                    statement={feesStatement}
                    student={profileInfo}
                    customRef={statementRef}
                  />
                )}

                {feesStatement === "404" && (
                  <p className="yx-muted text-center mb-0">No transactions found</p>
                )}

                {!feesStatement && <ActivityLoader />}
              </div>
            </div>
          </div>
        </div>


        
      </MainContent>
    </PageContainer>
    <AlertDateSelector
            show={showDateAlert}
            onClose={() => setShowDateAlert(false)}
            onConfirm={handleStatementDate}
        />
      <ConfirmationDialog
        show={showDelete}
        title="Delete Student"
        alertDescription={`Please confirm that you want to delete the student record. This action is irreversible.`}
        notes={`Students with billing records cannot be deleted. You must first delete all their billing records before deleting the student.`}
        buttonLabel="Delete Student"
        handleClose={() => setShowDelete(false)}
        handleConfirm={handleDelete}
      />

      <ConfirmationDialog
        show={showDeactivate}
        title="Deactivate Student"
        alertDescription={`Please confirm that you want to deactivate the student record. This action is reversible.`}
        notes={`Deactivating a student will not delete their records. However, they won't appear in the students' lists. They can only be found by searching for them.`}
        buttonLabel="Deactivate Student"
        handleClose={() => setShowDeactivate(false)}
        handleConfirm={handleDeactivate}
      />

      <ConfirmationDialog
        show={showActivate}
        title="Activate Student"
        alertDescription={`Please confirm that you want to activate the student record.`}
        notes={`Activating a student will make them appear in the students' lists.`}
        buttonLabel="Activate Student"
        handleClose={() => setShowActivate(false)}
        handleConfirm={handleActivate}
        themeClass="success"
      />
      <StudentAdvanceModal
        show={showAdvanceModal}
        onClose={() => setShowAdvanceModal(false)}
        studentId={studentId}
        student={profileInfo}
      />

    </>
  );
};

export default StudentProfilePage;
